'use strict';

// external imports
import React, {
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import Reference from '~/shared/src/ci/Reference';
import {getXmlMetaModelPayloadForContext} from '~/shared/src/utilities/xmlMetaModelExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {
    getXmlPayloadForContext,
    wrapDataXml
} from '~/shared/src/utilities/xmlExport';

// frontend imports
import MetaModelDesignerView, {
    ApplicationToYnItemTypeItemIdMapping
} from '~/frontend/src/modules/panels/MetaModelDesignerView';

import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    isDataModified
} from '~/frontend/src/utilities/dataUtils';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {setMessage} from '~/frontend/src/actions/intercom';


export type DefaultProps = {
    schema? : Schema;

    applicationToYnItemTypeItemIdMapping? : ApplicationToYnItemTypeItemIdMapping;

    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton? : boolean;

    saveButtonDisabled? : boolean;

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount? : number;

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip? : React.ReactNode;

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showAddButton? : boolean;

    addButtonDisabled? : boolean;

    /**
     * Show copy XML button.
     */
    showCopyXmlButton? : boolean;

    /**
     * Show download XML button.
     */
    showDownloadXmlButton? : boolean;

    /**
     * Show reload button.
     */
    showReloadButton? : boolean;

    showModifiedHint? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    schema                              : 'DATA',
    applicationToYnItemTypeItemIdMapping: {},
    saveButtonErrorCount                : 0,
    saveButtonErrorTooltip              : '',
    showAddButton                       : true,
    showSaveButton                      : true,
    showCopyXmlButton                   : true,
    showDownloadXmlButton               : true,
    showReloadButton                    : true,
    addButtonDisabled                   : false,
    saveButtonDisabled                  : false,
    showModifiedHint                    : false
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    dataContext : string;

    application? : string;

    onAddButtonClick? : () => void;

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const dispatch : AppDispatch  = useDispatch(),
          {
              addButtonDisabled,
              application,
              applicationToYnItemTypeItemIdMapping,
              dataContext,
              saveButtonDisabled,
              saveButtonErrorCount,
              saveButtonErrorTooltip,
              schema,
              showAddButton,
              showCopyXmlButton,
              showDownloadXmlButton,
              showModifiedHint,
              showReloadButton,
              showSaveButton,
              uid
          }                       = props,
          user                    = useSelector( ( state : StoreState ) => state.session?.user ),
          messageCatalogTimestamp = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          language                = user
              ? user.language
              : undefined,
          filteredItemTypeIds     = useMemo(
              () => {
                  return application
                      ? Object.keys( applicationToYnItemTypeItemIdMapping[ application ] ?? {} ).map( i => Number( i ) )
                      : undefined
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping
              ]
          ),
          onSaveButtonClick       = useCallback(
              () => {
                  void MetaModelDesignerView.doSaveData( uid, dispatch );
              },
              [
                  uid
              ]
          ),
          getExportXml            = useCallback(
              () => {
                  const copyDataContext                              = `${dataContext}_copy`,
                        applicationItemTypeId                        = metaModel[ 'CFG' ].getItemType( 'Application' )?.id ?? 0,
                        ynItemTypeItemTypeId                         = metaModel[ 'CFG' ].getItemType( 'YnItemType' )?.id,
                        ynItemTypeBelongsToApplicationReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'Application', 'belongsTo', 'src' )[ 0 ]?.id ?? 0,
                        applicationItems : Item[]                    = [],
                        applicationReferences : Reference[]          = [];

                  let xml : string = '';

                  // clone dataContext
                  for ( const item of Object.values( global.data[ dataContext ].items ) ) {
                      item.clone( undefined, undefined, copyDataContext );
                  }
                  for ( const reference of Object.values( global.data[ dataContext ].references ) ) {
                      reference.clone( undefined, undefined, copyDataContext );
                  }

                  // get Application items and YnItemType-belongsTo->Application references
                  // delete YnItemTypes not belonging to application if
                  for ( const item of Object.values( global.data[ copyDataContext ]?.items ?? {} ) ) {
                      if ( item.itemTypeId === applicationItemTypeId && ( !application || item.name === application ) ) {
                          applicationItems.push( item );

                          for ( const referenceId of item.references ) {
                              const reference = global.data[ copyDataContext ].references[ referenceId ];

                              if ( reference && reference.referenceSetId === ynItemTypeBelongsToApplicationReferenceSetId ) {
                                  applicationReferences.push( reference );
                              }
                          }
                      }
                      if ( item.itemTypeId === ynItemTypeItemTypeId && filteredItemTypeIds && !filteredItemTypeIds.includes( item.id ) ) {
                          item.statusId = metaModel[ 'CFG' ].getStatus( 'deleted' )!.id;
                      }
                  }
                  applicationItems.sort( ( a, b ) => a.key.toLowerCase() > b.key.toLowerCase() ? 1 : ( a.key.toLowerCase() < b.key.toLowerCase() ? -1 : 0 ) );

                  xml += `<metaModel schema='${schema.toLowerCase()}'>\r\n`.indent();
                  xml += getXmlMetaModelPayloadForContext( copyDataContext, undefined, undefined, true );
                  xml += `</metaModel>\r\n`.indent();
                  xml += `<data schema='${schema.toLowerCase()}'>\r\n`.indent();
                  xml += getXmlPayloadForContext( copyDataContext, applicationItems.map( item => item.id ), applicationReferences.map( reference => reference.id ), true );
                  xml += `</data>\r\n`.indent();

                  delete global.data[ copyDataContext ];

                  return wrapDataXml( xml );
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping,
                  dataContext,
                  filteredItemTypeIds,
                  schema
              ]
          ),
          onCopyXmlApply          = useCallback(
              async () => {
                  await navigator.clipboard.writeText( getExportXml() );

                  dispatch( setMessage(
                      uid,
                      'toast',
                      'xmlDataCopiedToClipboard',
                      {
                          severity: 'info',
                          summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                          detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                      }
                  ) );
              },
              [
                  getExportXml,
                  language,
                  messageCatalogTimestamp,
                  uid
              ]
          ),
          onDownloadXmlApply      = useCallback(
              () => {
                  fileDownload(
                      getExportXml(),
                      `MetaModelDesigner${application ? `_${application}` : ''}`,
                      'xml'
                  );
              },
              [
                  application,
                  getExportXml
              ]
          ),
          onAddButtonClick        = () => {
              props.onAddButtonClick?.();
          },
          onReloadButtonClick     = () => {
              props.onReloadButtonClick?.();
          };

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showSaveButton ) {
                layoutComponents.left.push( {
                    type  : 'saveButton',
                    saveButtonErrorCount,
                    saveButtonErrorTooltip,
                    params: {
                        key: `${uid}_saveButton`,

                        disabled: saveButtonDisabled,
                        onClick : onSaveButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showCopyXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'copyXml',
                    params: {
                        key: `${uid}_copyXmlButton`,

                        uid,

                        onExpand: () => {
                            void onCopyXmlApply();
                            return false;
                        },
                        onApply : () => {
                            void onCopyXmlApply();
                        }
                    }
                } );
            }

            if ( showDownloadXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXml',
                    params: {
                        key: `${uid}_downloadXmlButton`,

                        uid,

                        onExpand: () => {
                            onDownloadXmlApply();
                            return false;
                        },
                        onApply : onDownloadXmlApply
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showAddButton ) {
                layoutComponents.left.push( {
                    type  : 'button',
                    params: {
                        key: `${uid}_reloadButton`,

                        icon          : 'pi pi-plus-circle',
                        tooltip       : __( 'YN.base.frontend.metaModelDesigner.toolbar.addButton.label' ),
                        tooltipOptions: config.frontend?.buttonTooltipOptions,
                        className     : 'p-button-text p-button-success',
                        disabled      : addButtonDisabled,
                        onClick       : onAddButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModifiedHint ) {
                layoutComponents.left.push( {
                    type  : 'custom',
                    params: {
                        key      : `${uid}_modifiedHint`,
                        className: 'flex align-items-center',
                        component: <>
                            <div className="yn-toolbar-modified-hint-icon"/>
                            <span className="ml-2 yn-secondary">{__( 'YN.base.frontend.common.modified.hint' )}</span>
                        </>
                    }
                } );
            }


            // right
            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm: () => isDataModified( dataContext, 'CFG' ),
                        onClick: onReloadButtonClick
                    }
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />;
        },
        [
            addButtonDisabled,
            language,
            messageCatalogTimestamp,
            onAddButtonClick,
            onCopyXmlApply,
            onDownloadXmlApply,
            onSaveButtonClick,
            saveButtonDisabled,
            saveButtonErrorCount,
            saveButtonErrorTooltip,
            showAddButton,
            showCopyXmlButton,
            showDownloadXmlButton,
            showModifiedHint,
            showReloadButton,
            showSaveButton,
            uid
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'MetaModelDesigner-Toolbar';

export default Toolbar;
